import { useAuth } from '@/features/auth';
import { hasMinRole } from '@/shared/constants/auth/roles.constant';

import { usePumsemManageEditions } from '../api/use-pumsem-manage.query';
import { usePumsemEditions } from '../api/use-pumsem.query';
import type { PumsemEdition } from '../model/pumsem.type';

/**
 * 뷰어의 **관리자 오버레이**.
 *
 * ── 왜 오버레이인가 ─────────────────────────────────────────
 * "관리자에게는 숨긴 판도 보인다" 를 공개 경로에 분기로 걸 수 없다.
 * api-gateway 의 JwtAuthMiddleware 는 public path 를 만나면 `x-user-*` 를 심기
 * **전에** next() 하므로, 공개 라우트는 로그인 여부를 영영 알 수 없다.
 * 게이트웨이를 열어 해결할 수도 있지만 도움말까지 같은 전제를 쓰고 있어
 * 파급이 크고, 무엇보다 숨긴 판이 비인증 경로로 나갈 여지를 만든다.
 *
 * 그래서 **화면 쪽에서 목록을 한 번 더 부른다.** 관리자면 인증 경로로
 * 숨긴 판까지 받아 셀렉트를 그 목록으로 그리고, 숨긴 판을 고르면 상세·검색의
 * 출처도 관리 경로로 바꾼다. 공개 API 는 손대지 않는다.
 */
export function usePumsemAdminOverlay(edition: string | null) {
  const { user } = useAuth();
  const isAdmin = hasMinRole(user?.userRole, 'ADMIN');

  const { data: publicEditions } = usePumsemEditions();
  const { data: manageEditions } = usePumsemManageEditions(isAdmin);

  // 관리 목록은 공개판까지 포함한다 — 합칠 필요 없이 통째로 대신 쓴다.
  const editions: PumsemEdition[] =
    (isAdmin ? manageEditions : undefined) ?? publicEditions ?? [];

  /**
   * 공개판이 하나도 없다. 서버 `resolveEdition` 이 **이때만** 판 미지정을
   * 숨긴 판으로 떨어뜨리므로, 화면의 판정도 같은 규칙이어야 한다.
   */
  const noPublishedEdition =
    editions.length > 0 && editions.every((item) => item.isPublished === false);

  /**
   * 지금 보고 있는 판이 숨겨져 있는가.
   *
   * `edition` 이 null 이면 "최신 공개판" 이다 — 숨긴 판이 기본값이 되는 경우는
   * 공개판이 하나도 없을 때뿐이고, 그때도 배너는 떠야 한다. 관리자가 셀렉트에서
   * `최신 개정판` 을 골랐는데 초안이 열리면, 그 화면을 일반 사용자가 보는
   * 화면이라고 착각한 채 검수를 마친다.
   */
  const hidden =
    edition === null
      ? noPublishedEdition
      : editions.find((item) => item.slug === edition)?.isPublished === false;

  return {
    isAdmin,
    editions,
    /** 숨긴 판을 보고 있다 — 데이터 출처를 관리 경로로 바꿔야 한다. */
    viewingHidden: hidden,
    /** 판을 안 고른 채로 숨긴 판이 열린 상태. 배너 문구가 갈린다. */
    noPublishedEdition,
  };
}
